import { ApiError } from '@shared/lib/errors/domain-errors';
import { intentErrorKey } from '@shared/lib/errors/intent-error-i18n';
import { parseInsufficientFunds } from '@shared/lib/errors/insufficient-funds';
import { pinErrorKey } from '@shared/lib/errors/pin-error-i18n';
import { formatUnits } from '@shared/lib/format';
import { CARD_FUNDING_DECIMALS } from './card.types';

const ERROR_KEYS: Record<string, string> = {
  CARD_NOT_FOUND: 'cards.errors.notFound',
  CARD_LIMIT_REACHED: 'cards.errors.limitReached',
  CARD_TYPE_LIMIT_REACHED: 'cards.errors.typeLimitReached',
  CARD_EMAIL_LIMIT_REACHED: 'cards.errors.emailLimitReached',
  CARD_EMAIL_DOMAIN_FORBIDDEN: 'cards.errors.emailDomainForbidden',
  CARD_TYPE_UNAVAILABLE: 'cards.errors.typeUnavailable',
  CARD_ISSUANCE_RATE_LIMITED: 'cards.errors.issuanceRateLimited',
  CARD_QUOTE_INPUT_INVALID: 'cards.errors.quoteInputInvalid',
  CARD_TOPUP_BELOW_MIN: 'cards.errors.topupBelowMin',
  CARD_TOPUP_ABOVE_MAX: 'cards.errors.topupAboveMax',
  CARD_TOPUP_AMOUNT_TOO_SMALL: 'cards.errors.topupTooSmall',
  CARD_FROZEN: 'cards.errors.frozen',
  CARD_NOT_ACTIVE: 'cards.errors.notActive',
  CARD_HAS_BALANCE: 'cards.errors.hasBalance',
  CARD_FEATURE_NOT_SUPPORTED: 'cards.errors.featureNotSupported',
  CARD_NOT_ACTIONABLE: 'cards.errors.notActionable',
  CARD_FEE_RULE_MISSING: 'cards.errors.feeRuleMissing',
  CARD_OPERATION_IN_PROGRESS: 'cards.errors.operationInProgress',
  CARD_IDEMPOTENCY_KEY_TOO_LONG: 'cards.errors.generic',
  CARD_TOPUP_DISABLED: 'cards.errors.topupDisabled',
  CARD_TEMPORARILY_UNAVAILABLE: 'cards.errors.temporarilyUnavailable',
  CARDS_DISABLED: 'cards.errors.disabled',
  CARD_PROVIDER_UNAVAILABLE: 'cards.errors.providerUnavailable',
  CARD_FX_RATE_UNAVAILABLE: 'cards.errors.fxUnavailable',
  FRESH_TOKEN_MISSING: 'cards.errors.freshTokenMissing',
  WALLET_NOT_FOUND: 'cards.errors.walletNotFound',
  CARD_CHAIN_NOT_SUPPORTED: 'cards.errors.generic',
  CARD_TOKEN_NOT_SUPPORTED: 'cards.errors.generic',
  ONBOARDING_SEED_REQUIRED: 'cards.errors.onboardingRequired',
  CARD_PROVIDER_CARD_INACTIVE: 'cards.errors.providerCardInactive',
  CARD_PROVIDER_CARD_CANCELED: 'cards.errors.providerCardCanceled',
  CARD_PROVIDER_CARD_NOT_FOUND: 'cards.errors.notFound',
  CARD_PROVIDER_TOPUP_LIMIT_EXCEEDED: 'cards.errors.providerTopupLimit',
  CARD_PROVIDER_TOPUP_NOT_ALLOWED: 'cards.errors.providerTopupNotAllowed',
  CARD_PROVIDER_MISSED_REQUIREMENTS: 'cards.errors.providerRequirements',
  CARD_3DS_CODE_NOT_FOUND: 'cards.errors.threeDsCodeNotFound',
  CARD_DEPOSIT_FEE_UNAVAILABLE: 'cards.errors.depositFeeUnavailable',
  CARD_SETTLEMENT_HALTED: 'cards.errors.settlementHalted',
};

const DEGRADATION_CODES = new Set([
  'CARDS_DISABLED',
  'CARD_SETTLEMENT_HALTED',
  'CARD_TEMPORARILY_UNAVAILABLE',
  'CARD_PROVIDER_UNAVAILABLE',
  'CARD_TOPUP_DISABLED',
  'CARD_FEE_RULE_MISSING',
  'CARD_PROVIDER_MERCHANT_NOT_FOUND',
  'CARD_PROVIDER_MERCHANT_DEGRADED',
  'CARD_PROVIDER_INSUFFICIENT_POOL_BALANCE',
  'CARD_PROVIDER_ACCOUNT_DISABLED',
  'CARD_PROVIDER_RATE_LIMITED',
  'CARD_PROVIDER_RESPONSE_INVALID',
  'CARD_PROVIDER_AUTH_FAILED',
  'CARD_PROVIDER_AUTH_INVALID',
  'CARD_BRIDGE_UNAVAILABLE',
]);

export interface CardErrorText {
  readonly key: string;
  readonly params: Record<string, string>;
}

export interface CardAssetDecimals {
  readonly native: number | null;
  readonly token: number | null;
}

/**
 * `decimals` come from the balance list rather than a constant (TRON-001 §4);
 * the constant is the fallback for an error that arrives before balances do.
 */
export function cardShortfall(error: unknown, decimals?: CardAssetDecimals): CardErrorText | null {
  const shortfall = parseInsufficientFunds(error);
  if (!shortfall?.attributed) return null;

  const isNative = shortfall.asset === 'native';
  const known = isNative ? decimals?.native : decimals?.token;
  const precision = known ?? CARD_FUNDING_DECIMALS;

  const amount = (raw: string): string => {
    try {
      return formatUnits(BigInt(raw), precision);
    } catch {
      return raw;
    }
  };

  return {
    key: isNative ? 'cards.errors.insufficientGas' : 'cards.errors.insufficientToken',
    params: { required: amount(shortfall.required), available: amount(shortfall.available) },
  };
}

export function cardErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.errorCode : null;
}

export function cardErrorKey(error: unknown): string {
  if (error instanceof Error && 'errorKey' in error && typeof error.errorKey === 'string') {
    return error.errorKey;
  }
  const pinKey = pinErrorKey(error);
  if (pinKey) return pinKey;
  const code = cardErrorCode(error);
  if (code && ERROR_KEYS[code]) return ERROR_KEYS[code];
  const intentKey = intentErrorKey(error);
  if (intentKey) return intentKey;
  if (error instanceof ApiError && error.status === 422) return 'cards.errors.insufficientFunds';
  if (error instanceof ApiError && error.status === 429) return 'cards.errors.tooManyRequests';
  return 'cards.errors.generic';
}

/** Which issue-wizard step a `POST /cards` rejection should send the user back to. */
export type IssueErrorTarget = 'email' | 'type' | null;

const EMAIL_STEP_CODES = new Set(['CARD_EMAIL_LIMIT_REACHED', 'CARD_EMAIL_DOMAIN_FORBIDDEN']);
const TYPE_STEP_CODES = new Set([
  'CARD_LIMIT_REACHED',
  'CARD_TYPE_LIMIT_REACHED',
  'CARD_TYPE_UNAVAILABLE',
]);

export function issueErrorTarget(error: unknown): IssueErrorTarget {
  const code = cardErrorCode(error);
  if (code === null) return null;
  if (EMAIL_STEP_CODES.has(code)) return 'email';
  if (TYPE_STEP_CODES.has(code)) return 'type';
  return null;
}

export function isDegraded(error: unknown): boolean {
  const code = cardErrorCode(error);
  return code !== null && DEGRADATION_CODES.has(code);
}

export function retryAfterSeconds(error: unknown): number | null {
  if (!(error instanceof ApiError)) return null;
  if (error.retryAfterSeconds !== null) return error.retryAfterSeconds;
  const match = /\btry again in (\d+)s\b/i.exec(error.message);
  return match?.[1] ? Number(match[1]) : null;
}
